import { AutoFocusPlugin } from '@lexical/react/LexicalAutoFocusPlugin';
import { ClearEditorPlugin } from '@lexical/react/LexicalClearEditorPlugin';
import {
  InitialConfigType,
  InitialEditorStateType,
  LexicalComposer,
} from '@lexical/react/LexicalComposer';
import { ContentEditable } from '@lexical/react/LexicalContentEditable';
import { EditorRefPlugin } from '@lexical/react/LexicalEditorRefPlugin';
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary';
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin';
import { HorizontalRuleNode } from '@lexical/react/LexicalHorizontalRuleNode';
import { HorizontalRulePlugin } from '@lexical/react/LexicalHorizontalRulePlugin';
import { LinkPlugin } from '@lexical/react/LexicalLinkPlugin';
import { ListPlugin } from '@lexical/react/LexicalListPlugin';
import { MarkdownShortcutPlugin } from '@lexical/react/LexicalMarkdownShortcutPlugin';
import { OnChangePlugin } from '@lexical/react/LexicalOnChangePlugin';
import { PlainTextPlugin } from '@lexical/react/LexicalPlainTextPlugin';
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin';
import { makePostLexicalNodes } from '@synoptic/lexical-nodes/make-post-lexical-nodes.ts';
import { globalConfigureMentionNode } from '@synoptic/lexical-nodes/mention.ts';
import { inputError, inputLabel } from '@synoptic/ui-kit/input/input.css.js';
import { useFocusOutside } from '@synoptic/ui-kit/utils/use-focus-outside.ts';
import clsx from 'clsx';
import { LexicalEditor } from 'lexical';
import { useId, useState } from 'react';
import { AutoLinkPlugin } from './plugins/auto-link';
import { CharacterLimitPlugin } from './plugins/character-limit-plugin/plugin';
import { ClickableLinkPlugin } from './plugins/clickable-link';
import { FloatingLinkEditorPlugin } from './plugins/floating-link-editor/plugin';
import { FloatingTextFormatToolbarPlugin } from './plugins/floating-menu/plugin';
import { HighlightPlugin } from './plugins/highlight/plugin';
import { HighlightPattern } from './plugins/highlight/types';
import { decorateMentionNode } from './plugins/mentions/decorate';
import { MentionsPlugin } from './plugins/mentions/plugin';
import { $prepopulateJson } from './utils/prepopulate-json';
import { validateUrl } from './utils/url';
import {
  editorContainer,
  editorContentEditableContainer,
  editorPlaceholder,
} from './text-editor.css';
import { baseTextEditorTheme } from './themes/base.css';
import { smallTextEditorTheme } from './themes/small.css';
import { textareaTheme } from './themes/textarea.css';

globalConfigureMentionNode({
  decorate: decorateMentionNode,
});

const nodes = makePostLexicalNodes();

type TextEditorProps = Omit<
  React.ComponentPropsWithoutRef<'div'>,
  'onChange'
> & {
  namespace: string;
  contentEditableClassName?: string;
  placeholder?: string;
  autoFocus?: boolean;
  onChange?: React.ComponentPropsWithoutRef<typeof OnChangePlugin>['onChange'];
  editable: boolean;
  charLimit?: number;
  withShowMore?: boolean;
  editorRef?: React.Ref<LexicalEditor>;
  theme?: 'base' | 'textarea' | 'small';
  initialState?: InitialEditorStateType;
  format?: 'json' | 'md';
  disabled?: boolean;
  label?: string;
  errorMessage?: string;
  highlightPatterns?: HighlightPattern[];
  /**
   * Callback to be called when focus moves outside of the editor's react tree including portals
   */
  onFocusOutside?: (e: FocusEvent) => void;

  rich?: boolean;

  forceExpand?: boolean;
  expanded?: boolean;
  onExpandedChange?: (expanded: boolean) => void;
};

const themes = {
  base: baseTextEditorTheme,
  textarea: textareaTheme,
  small: smallTextEditorTheme,
} as const;

const TextEditorComponent = ({
  placeholder,
  autoFocus,
  onChange,
  editable,
  initialState,
  editorRef,
  theme = 'base',
  className,
  namespace,
  contentEditableClassName,
  charLimit,
  withShowMore,
  rich = false,
  disabled,
  highlightPatterns,
  onFocusOutside,
  format = 'json',
  label,
  errorMessage,
  forceExpand,
  expanded: expandedProp,
  onExpandedChange,

  ...containerDivProps
}: TextEditorProps) => {
  const initialConfig: InitialConfigType = {
    namespace,
    theme: themes[theme],
    nodes: [...nodes, HorizontalRuleNode],
    onError(err) {
      throw err;
    },
    // TODO: react to editable updates https://lexical.dev/docs/concepts/read-only#setting-the-mode
    editable: editable && !disabled,
    editorState:
      typeof initialState === 'string'
        ? (editor) => $prepopulateJson(editor, initialState, format)
        : initialState,
  };

  const contentEditableId = useId();

  const [uncontrolledExpanded, setUncontrolledExpanded] = useState(false);
  const expanded = expandedProp ?? uncontrolledExpanded;
  const setExpanded = onExpandedChange ?? setUncontrolledExpanded;
  const isExpanded = expanded || !!forceExpand;

  const [anchorElem, setAnchorElem] = useState<HTMLElement | null>(null);

  const [isLinkEditMode, setIsLinkEditMode] = useState(false);

  const { onFocusCapture, onBlurCapture } = useFocusOutside(onFocusOutside);

  const contentEditable = (
    // extra div is needed because of https://github.com/facebook/lexical/pull/6901
    <div className={editorContentEditableContainer}>
      <ContentEditable
        id={contentEditableId}
        placeholder={
          <div
            className={clsx(contentEditableClassName, editorPlaceholder[theme])}
          >
            {placeholder}
          </div>
        }
        aria-placeholder={placeholder || ''}
        className={contentEditableClassName}
        disabled={disabled}
        tabIndex={disabled ? undefined : 0}
        aria-label={label}
      />
    </div>
  );

  return (
    <LexicalComposer initialConfig={initialConfig}>
      <div
        onFocusCapture={onFocusCapture}
        onBlurCapture={onBlurCapture}
        ref={setAnchorElem}
        className={clsx(editorContainer, className)}
        {...containerDivProps}
      >
        {label && (
          <label className={inputLabel} aria-hidden>
            {label}
          </label>
        )}
        {rich ? (
          <>
            <RichTextPlugin
              contentEditable={contentEditable}
              ErrorBoundary={LexicalErrorBoundary}
            />
            <ListPlugin />
            {anchorElem ? (
              <>
                <FloatingTextFormatToolbarPlugin
                  setIsLinkEditMode={setIsLinkEditMode}
                  anchorElem={anchorElem}
                />
                <FloatingLinkEditorPlugin
                  setIsLinkEditMode={setIsLinkEditMode}
                  anchorElem={anchorElem}
                  isLinkEditMode={isLinkEditMode}
                />
              </>
            ) : null}
          </>
        ) : (
          <PlainTextPlugin
            contentEditable={contentEditable}
            ErrorBoundary={LexicalErrorBoundary}
          />
        )}
        <HistoryPlugin />
        {autoFocus ? <AutoFocusPlugin /> : null}
        <AutoLinkPlugin />
        <LinkPlugin validateUrl={rich ? validateUrl : undefined} />
        <HorizontalRulePlugin />
        {charLimit ? (
          <CharacterLimitPlugin
            expanded={isExpanded}
            charLimit={charLimit}
            withShowMore={withShowMore && !forceExpand}
            setExpanded={setExpanded}
          />
        ) : null}
        {editable && rich ? <MarkdownShortcutPlugin /> : null}
        {editable ? null : <ClickableLinkPlugin />}
        {editable ? <MentionsPlugin /> : null}
        {highlightPatterns ? (
          <HighlightPlugin patterns={highlightPatterns} />
        ) : null}
        {onChange ? (
          <OnChangePlugin onChange={onChange} ignoreSelectionChange />
        ) : null}
        {editorRef ? <EditorRefPlugin editorRef={editorRef} /> : null}
        <ClearEditorPlugin />

        {errorMessage && <p className={inputError}>{errorMessage}</p>}
      </div>
    </LexicalComposer>
  );
};

export const TextEditor = (props: TextEditorProps) => {
  // TextEditor is uncontrolled and can't be updated when new initialValue is passed
  // this can lead to stale data when navigating from one post page to another or inside virtualized feeds
  // remount editor when rendering in a new place
  return <TextEditorComponent key={props.namespace} {...props} />;
};
